import { useState, useEffect } from "react";
import { Heart, Minus, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart, lineId } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useWishlist } from "../contexts/WishlistContext";
import { api, formatINR } from "../lib/api";
import { ProductImage } from "./ProductImage";

export function ProductCard({ product }) {
  const { add, inc, dec, qtyOf } = useCart();
  const { user } = useAuth();
  const { ids, toggle } = useWishlist();
  const navigate = useNavigate();

  const variants = (product.variants || []).filter((v) => v.active !== false);
  const hasVariants = variants.length > 0;
  const [selected, setSelected] = useState(variants[0] || null);
  const [notified, setNotified] = useState(false);

  useEffect(() => {
    setSelected(variants[0] || null);
    setNotified(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  const price = selected ? Number(selected.price) : Number(product.price);
  const mrp = selected ? Number(selected.mrp || 0) : Number(product.mrp || 0);
  const stock = selected ? Number(selected.stock) : Number(product.stock);
  const qty = qtyOf(product.id, selected?.id);
  const lid = lineId(product.id, selected?.id);
  const isSaved = ids.includes(product.id);
  const off = mrp && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

  const notifyMe = async () => {
    if (!user || user === false) {
      navigate(`/login?next=${encodeURIComponent(`/products/${product.id}`)}`);
      return;
    }
    try {
      await api.post(`/products/${product.id}/notify`, { variant_id: selected?.id || null });
      setNotified(true);
      toast.success("We'll notify you when it's back in stock");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    }
  };

  const saveProduct = async () => {
    if (!user || user === false) {
      navigate(`/login?next=${encodeURIComponent(`/products/${product.id}`)}`);
      return;
    }
    try {
      const saved = await toggle(product.id);
      toast.success(saved ? "Saved to your wishlist" : "Removed from your wishlist");
    } catch {
      toast.error("We could not update your wishlist. Please try again.");
    }
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className={
        "group relative flex flex-col rounded-2xl border border-stone-200 bg-white p-3 shadow-sm " +
        "transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 " +
        "hover:border-orange-200 hover:shadow-lg"
      }
    >
      {off > 0 && (
        <span className="absolute left-2 top-2 z-10 rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          {off}% off
        </span>
      )}
      <button
        type="button"
        onClick={saveProduct}
        aria-label={isSaved ? "Remove from wishlist" : "Save to wishlist"}
        className={`absolute right-2 top-2 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/90 shadow-sm
          transition-colors hover:text-orange-700 ${isSaved ? "text-orange-600" : "text-stone-600"}`}
        data-testid={`wishlist-toggle-${product.id}`}
      >
        <Heart className="h-4 w-4" fill={isSaved ? "currentColor" : "none"} />
      </button>
      <Link
        to={`/products/${product.id}`}
        className="aspect-square w-full overflow-hidden rounded-xl bg-stone-50"
        data-testid={`product-detail-link-${product.id}`}
      >
        <ProductImage
          src={product.image}
          alt={product.name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          testId={`product-image-${product.id}`}
        />
      </Link>
      <div className="mt-3 flex flex-1 flex-col">
        {hasVariants ? (
          <div className="mb-1.5 flex flex-wrap gap-1" data-testid={`variant-pills-${product.id}`}>
            {variants.map((v) => {
              const sel = selected?.id === v.id;
              const soldOut = Number(v.stock) <= 0;
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={soldOut}
                  onClick={(e) => { e.preventDefault(); setSelected(v); setNotified(false); }}
                  data-testid={`card-variant-${product.id}-${v.id}`}
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-bold transition-colors ${
                    sel
                      ? "border-[#FF5200] bg-orange-50 text-[#FF5200]"
                      : "border-stone-300 bg-white text-stone-600 hover:border-orange-300"
                  } ${soldOut ? "cursor-not-allowed opacity-40" : ""}`}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-stone-500">{product.unit}</p>
        )}
        <Link
          to={`/products/${product.id}`}
          className="mt-0.5 line-clamp-2 text-sm font-semibold text-stone-900 hover:text-orange-700"
          data-testid={`product-name-link-${product.id}`}
        >
          {product.name}
        </Link>
        <div className="mt-auto flex items-center justify-between pt-3">
          <div>
            <p className="text-base font-semibold text-stone-900" data-testid={`card-price-${product.id}`}>
              {formatINR(price)}
            </p>
            {off > 0 && <p className="text-xs text-stone-400 line-through">{formatINR(mrp)}</p>}
          </div>
          {qty > 0 ? (
            <div className="flex min-h-10 items-center gap-2 rounded-lg bg-slate-100 px-1 py-1 shadow-sm">
              <button
                data-testid={`dec-${product.id}`}
                onClick={() => dec(lid)}
                className="rounded-md p-1.5 font-bold text-slate-900 active:scale-95"
              >
                <Minus size={14} />
              </button>
              <span
                data-testid={`qty-${product.id}`}
                className="min-w-[16px] text-center text-sm font-semibold text-stone-800"
              >
                {qty}
              </span>
              <button
                data-testid={`inc-${product.id}`}
                onClick={() => inc(lid)}
                className="rounded-md p-1.5 font-bold text-slate-900 active:scale-95"
              >
                <Plus size={14} />
              </button>
            </div>
          ) : stock > 0 ? (
            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={() => add(product, selected)}
              className={
                "min-h-10 rounded-lg bg-[#FF5200] px-4 text-sm font-bold text-white shadow-sm " +
                "transition-transform hover:bg-[#FF6A00] active:scale-95"
              }
            >
              + Add
            </button>
          ) : (
            <button
              type="button"
              data-testid={`notify-me-${product.id}`}
              onClick={notifyMe}
              disabled={notified}
              className={`min-h-9 rounded-full border px-3 text-xs font-semibold transition-colors ${
                notified
                  ? "border-green-300 bg-green-50 text-green-700"
                  : "border-stone-300 bg-white text-stone-700 hover:border-orange-400 hover:text-orange-700"
              }`}
            >
              {notified ? "Notified ✓" : "Notify me"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
